import { Enum } from '../../../../react-commons/types/enum';
import { assertUnreachable } from '../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../resource/enumerations/ResourceType';
import { NamedArtifactResource } from '../../resource/resource-base/artifactResourceTypes';
import { DeepKeys } from '@tanstack/react-table';

export enum FilterType {
    NAME = 'NAME',
    LONG_NAME = 'LONG_NAME',
    SHORT_NAME = 'SHORT_NAME',
    TYPE = 'TYPE',
    CATEGORY = 'CATEGORY',
    VARIANT = 'VARIANT',
    UNUSED = 'UNUSED',
    HAS_OBJECT_ID = 'HAS_OBJECT_ID',
    SYSTEM_CONSTANT = 'SYSTEM_CONSTANT',
    VALIDATION = 'VALIDATION',
}

export enum InterfaceRadioButton {
    SELECT = 'SELECT',
    CREATE = 'CREATE',
}

export interface SelectedFilter {
    type: FilterType;
    value: any;
    typePropertyName?: TypePropertyName;
}

export const getFilterCallback: (selectedFilter: SelectedFilter) => (resource: any, secondaryResource?: any) => boolean = selectedFilter => {
    switch (selectedFilter.type) {
        case FilterType.NAME:
            return (resource: any, secondaryResource?: any) => filterCaseInsensitive(resource.name ?? secondaryResource.name, selectedFilter.value);
        case FilterType.SHORT_NAME:
            return (resource: any) => filterCaseInsensitive(resource.shortName, selectedFilter.value);
        case FilterType.LONG_NAME:
            return (resource: any, secondaryResource?: any) => filterCaseInsensitive(resource.longName ?? secondaryResource.longName, selectedFilter.value);
        case FilterType.TYPE:
            return (resource: any, secondaryResource?: any) =>
                (resource[selectedFilter.typePropertyName ?? 'type'] ?? secondaryResource[selectedFilter.typePropertyName ?? 'type']) === selectedFilter.value;
        case FilterType.CATEGORY:
            return (resource: any) => resource.category === selectedFilter.value;
        case FilterType.VARIANT:
            return (_: any) => true;
        case FilterType.UNUSED:
            return (resource: any) => selectedFilter.value.includes(resource.id);
        case FilterType.HAS_OBJECT_ID:
            return (resource: any) => selectedFilter.value.includes(resource.objectId);
        case FilterType.SYSTEM_CONSTANT:
            return (_: any) => true;
        case FilterType.VALIDATION:
            return (_: any) => true;
        default: {
            return assertUnreachable(selectedFilter.type);
        }
    }
};

export const filterCaseInsensitive = (text: string, filter: string): boolean => {
    return text.toLowerCase().includes(filter.toLocaleLowerCase());
};

export interface OverviewComponentPropertyValidationInformation {
    namePropertyIdentifier: string;
    typeOrCategoryPropertyIdentifier?: string;
    resourceType: ResourceType;
    systemConstantPropertyIdentifier?: string;
}

export interface EnumProperties<E extends string, R extends object> {
    enumType: Enum<E>;
    enumPropertyName: TypeAndCategoryPropertyName & keyof R & DeepKeys<R>;
    enumFilter?: E;
    enumArrayFilter?: Array<E>;
}

export type TypePropertyName = 'type' | 'parameterType' | 'interfaceType' | 'portType' | 'connectorType';
export type TypeAndCategoryPropertyName = TypePropertyName | 'category';
export type TypedOrCategorized<E extends string> = {
    [key in TypeAndCategoryPropertyName]?: E;
};
export type TypedOrCategorizedNamedResource<E extends string> = TypedOrCategorized<E> & NamedArtifactResource;
export type TypedOrCategorizedNamedCreationResource<E extends string> = TypedOrCategorized<E> & { name: string };

export const formatTypePropertyName = (propertyName: TypeAndCategoryPropertyName): string => {
    return (propertyName.charAt(0).toUpperCase() + propertyName.slice(1)).split(/(?=[A-Z])/).join(' ');
};
